import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { api, resolveImg } from "@/lib/api";
import { DISCIPLINE_COLORS } from "@/lib/constants";
import { Users, Flame, Dumbbell, MapPin, Swords, Wind, Hand, Check, Clock, Calendar } from "lucide-react";

const WORK_ICON = { "Hard Sparring": Swords, "Technical/Flow Sparring": Wind, "Padwork/Clinch": Hand };

const CAMP_STATUS = {
  planning: { label: "Planning", cls: "text-yellow-400 border-yellow-400/40 bg-yellow-400/5" },
  active: { label: "Active", cls: "text-volt border-volt/40 bg-volt/10" },
  complete: { label: "Complete", cls: "text-zinc-400 border-zinc-500/40 bg-zinc-500/5" },
};

function fmtRange(s, e) {
  if (!s && !e) return null;
  const f = (d) => new Date(d + "T00:00:00").toLocaleDateString("en-GB", { day: "numeric", month: "short" });
  if (s && e) return `${f(s)} – ${f(e)}`;
  return f(s || e);
}

export default function MyCamp() {
  const { data, isLoading } = useQuery({
    queryKey: ["my-stable"],
    queryFn: async () => (await api.get("/my-stable")).data,
  });

  if (isLoading) {
    return <div className="text-center py-20 text-volt font-heading uppercase tracking-widest animate-pulse">Loading…</div>;
  }

  const trainer = data?.trainer;
  const camps = data?.camps || [];

  if (!trainer) {
    return (
      <div className="text-center py-20 border border-dashed border-[#27272a] rounded-2xl" data-testid="no-trainer">
        <Users size={32} className="mx-auto text-zinc-700 mb-3" />
        <p className="font-heading text-xl uppercase tracking-wide text-zinc-400">You're not in a stable yet</p>
        <p className="text-sm text-zinc-600 mt-1">When a gym trainer invites you, it'll appear in your Requests. Once you accept, your camps show up here.</p>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="my-camp">
      {/* Trainer */}
      <div className="bg-card border border-volt/30 rounded-2xl p-5 flex items-center gap-4">
        <div className="w-16 h-16 rounded-xl overflow-hidden border border-[#27272a] bg-[#0d0d0d] flex items-center justify-center shrink-0">
          {trainer.avatar_url ? <img src={resolveImg(trainer.avatar_url)} alt="" className="w-full h-full object-cover" />
            : <span className="font-heading text-2xl text-zinc-700">{trainer.name?.[0]}</span>}
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-volt">Your Trainer</div>
          <h2 className="font-heading text-2xl uppercase tracking-tight">{trainer.name}</h2>
          <div className="flex items-center gap-3 text-sm text-zinc-400 mt-0.5">
            <span className="flex items-center gap-1"><Dumbbell size={13} className="text-volt" />{trainer.gym_name}</span>
            <span className="flex items-center gap-1"><MapPin size={12} />{trainer.city}</span>
          </div>
        </div>
      </div>

      {/* Camps */}
      <div>
        <h3 className="font-heading text-2xl uppercase tracking-tight mb-3 flex items-center gap-2"><Flame size={20} className="text-volt" /> Your Camps</h3>
        {camps.length === 0 ? (
          <p className="text-sm text-zinc-600">Your trainer hasn't built a camp for you yet.</p>
        ) : (
          <div className="space-y-6">
            {camps.map((c) => (
              <motion.div key={c.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-card border border-white/10 rounded-2xl p-5" data-testid={`my-camp-${c.id}`}>
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h4 className="font-heading text-xl uppercase tracking-tight">{c.name}</h4>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${(CAMP_STATUS[c.status] || CAMP_STATUS.planning).cls}`}>{(CAMP_STATUS[c.status] || CAMP_STATUS.planning).label}</span>
                      {fmtRange(c.start_date, c.end_date) && <span className="text-[11px] text-zinc-400 flex items-center gap-1"><Calendar size={11} className="text-volt" />{fmtRange(c.start_date, c.end_date)}</span>}
                    </div>
                  </div>
                  <span className="text-xs uppercase tracking-wider text-volt">{c.prospects.length} partner{c.prospects.length === 1 ? "" : "s"}</span>
                </div>
                {c.prospects.length === 0 ? (
                  <p className="text-sm text-zinc-600">No sparring partners added yet.</p>
                ) : (
                  <div className="grid sm:grid-cols-2 gap-3">
                    {c.prospects.map((p) => {
                      const WI = WORK_ICON[p.work_type] || Swords;
                      const dColor = DISCIPLINE_COLORS[p.discipline] || "#00FF66";
                      return (
                        <div key={p.invite_id} className="flex items-center gap-3 p-3 bg-[#0d0d0d] border border-[#27272a] rounded-xl">
                          <div className="w-11 h-11 rounded-lg overflow-hidden bg-[#161616] flex items-center justify-center shrink-0">
                            {p.avatar_url ? <img src={resolveImg(p.avatar_url)} alt="" className="w-full h-full object-cover" /> : <span className="font-heading text-zinc-700">{p.fighter_name?.[0]}</span>}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-heading uppercase tracking-wide text-sm truncate">{p.fighter_name}</div>
                            <div className="flex items-center gap-1 text-[11px] text-zinc-500"><WI size={11} />{p.work_type}</div>
                            <div className="flex items-center gap-1.5 mt-1">
                              <span className="text-[10px] uppercase px-1.5 py-0.5 rounded" style={{ background: dColor, color: "#000" }}>{p.discipline}</span>
                              <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${p.prospect_status === "accepted" ? "text-volt border border-volt/40" : "text-yellow-400 border border-yellow-400/40"}`}>
                                {p.prospect_status === "accepted" ? <Check size={9} /> : <Clock size={9} />}{p.prospect_status}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
